import { BackendHttpService } from '../../../../../services/backendHttpService';
import { WorkExperience, WorkExperienceCreateUpdate, WorkExperienceUpdate } from '../models/workExperience.model';

/**
 * Service for managing work experience data
 * Handles CRUD operations for work experience records
 */
export class WorkExperienceService extends BackendHttpService {
  constructor() {
    super('WorkExperience');
  }

  /**
   * Get all work experience records for a specific user
   * @param userId - The ID of the user
   * @returns Promise<WorkExperience[]> - Array of work experience records
   */
  async getWorkItemsByUserID(userId: number): Promise<WorkExperience[]> {
    try {
      return await this.get<WorkExperience[]>(`/byUserId/${userId}`);
    } catch (error) {
      console.error('Error getting work experience by user ID:', error);
      throw error;
    }
  }

  /**
   * Get a specific work experience record by ID
   * @param id - The work experience ID
   * @returns Promise<WorkExperience> - The work experience record
   */
  async getWorkExperienceById(id: number): Promise<WorkExperience> {
    try {
      return await this.get<WorkExperience>(id.toString());
    } catch (error) {
      console.error(`Error getting work experience with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new work experience record
   * @param workExperience - The work experience data to create
   * @returns Promise<WorkExperience> - The created work experience record
   */
  async addWorkExperience(workExperience: WorkExperienceCreateUpdate): Promise<WorkExperience> {
    try {
      return await this.post<WorkExperienceCreateUpdate, WorkExperience>(workExperience);
    } catch (error) {
      console.error('Error adding work experience:', error);
      throw error;
    }
  }

  /**
   * Update an existing work experience record
   * @param id - The work experience ID
   * @param workExperience - The updated work experience data
   * @returns Promise<WorkExperience> - The updated work experience record
   */
  async updateWorkExperience(id: number, workExperience: WorkExperienceCreateUpdate): Promise<WorkExperience> {
    try {
      // Create update payload with ID included
      const updatePayload: WorkExperienceUpdate = {
        ...workExperience,
        id
      };
      
      // Call PUT without ID in URL (ID is in the request body)
      return await this.put<WorkExperienceUpdate, WorkExperience>(updatePayload);
    } catch (error) {
      console.error('Error updating work experience:', error);
      throw error;
    }
  }

  /**
   * Delete a work experience record
   * @param id - The work experience ID to delete
   * @returns Promise<void>
   */
  async deleteWorkExperience(id: number): Promise<void> {
    try {
      await this.delete(id.toString());
    } catch (error) {
      console.error(`Error deleting work experience with id ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get dictionary of employers for autocomplete functionality
   * @returns Promise<string[]> - Array of employer names
   */
  async getEmployersDictionary(): Promise<string[]> {
    try {
      return await this.get<string[]>('/employersDictionary');
    } catch (error) {
      console.error('Error getting employers dictionary:', error);
      throw error;
    }
  }
} 